import { useEffect, useRef, useState } from "react";
import { academyRequest } from "./academyApi";
import { checkIdentitySession } from "./identityClient";
import { useIdentity } from "./useIdentity";
import { IdentitySecurityControls } from "./IdentitySecurityControls";
import type {
  IdentityAssignmentOptions,
  IdentityInvitationCommand,
  IdentityRoleGrantCommand,
  IdentityInvitationReceipt,
  IdentityRoleGrantReceipt,
} from "./generated/native-identity";

export const IDENTITY_DIRECTORY_CHANGED = "academy-identity-directory-changed";
export function IdentityAssignmentFields({
  operation,
  onClose,
}: {
  operation: "invitation" | "role-grant";
  onClose: () => void;
}) {
  const account = useIdentity();
  const context = `${account.session?.tenantId}:${account.session?.user.id}:${account.session?.workspace.id}`;
  return <AssignmentFields key={`${context}:${operation}`} operation={operation} onClose={onClose} />;
}
function AssignmentFields({
  operation,
  onClose,
}: {
  operation: "invitation" | "role-grant";
  onClose: () => void;
}) {
  const account = useIdentity();
  const [options, setOptions] = useState<IdentityAssignmentOptions | null>(null);
  const [scopeIndex, setScopeIndex] = useState("");
  const [role, setRole] = useState("");
  const [recipient, setRecipient] = useState("");
  const [reason, setReason] = useState("");
  const [expiry, setExpiry] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState<IdentityInvitationReceipt | null>(null);
  const retry = useRef<{ payload: string; key: string } | null>(null);
  const locked = useRef(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    setOptions(null);
    setError("");
    void (async () => {
      await checkIdentitySession();
      const data = await academyRequest<IdentityAssignmentOptions>("identity/assignment-options");
      const session = await checkIdentitySession();
      if (data.workspaceId !== session.workspace.id)
        throw new Error("Workspace changed. Reopen this dialog.");
      if (active) setOptions(data);
    })().catch(e => {
      if (active) setError(e.message);
    });
    return () => {
      active = false;
    };
  }, [revision]);
  const choices = options?.choices.filter(c => c.operation === operation) || [];
  const scope = scopeIndex ? choices[Number(scopeIndex)] : undefined;
  const localDate = (iso: string) => {
    const date = new Date(iso);
    return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
  };
  const submit = async () => {
    if (locked.current || !scope || !options || receipt) return;
    locked.current = true;
    setBusy(true);
    setError("");
    try {
      const expiresAt = new Date(expiry).toISOString();
      if (
        Date.parse(expiresAt) <= Date.now() ||
        (scope.expiresAt && Date.parse(expiresAt) > Date.parse(scope.expiresAt))
      )
        throw new Error("Choose a future expiry within your delegated authority.");
      if (!scope.roles.some(r => r.key === role)) throw new Error("Choose an available role.");
      const session = await checkIdentitySession();
      if (session.workspace.id !== options.workspaceId)
        throw new Error("Workspace changed. Reopen this dialog.");
      const common = {
        workspaceId: options.workspaceId,
        roleKey: role,
        scopeType: scope.scopeType,
        scopeId: scope.scopeId,
        reason: reason.trim(),
        expiresAt,
      };
      const input: IdentityInvitationCommand | IdentityRoleGrantCommand =
        operation === "invitation"
          ? { ...common, email: recipient.trim() }
          : { ...common, userId: recipient };
      const payload = JSON.stringify(input);
      if (retry.current?.payload !== payload) retry.current = { payload, key: crypto.randomUUID() };
      if (operation === "invitation") {
        const result = await academyRequest<IdentityInvitationReceipt>(
          "auth/invitations",
          input,
          "POST",
          true,
          retry.current.key,
        );
        if (!result.invitationId || !result.token)
          throw new Error("The invitation receipt could not be confirmed. Retry with the same details.");
        setReceipt(result);
      } else {
        const result = await academyRequest<IdentityRoleGrantReceipt>(
          "auth/role-grants",
          input,
          "POST",
          true,
          retry.current.key,
        );
        if (!result.grantId)
          throw new Error("The role assignment receipt could not be confirmed. Retry with the same details.");
        onClose();
      }
      window.dispatchEvent(new Event(IDENTITY_DIRECTORY_CHANGED));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Identity action failed.");
    } finally {
      setBusy(false);
      locked.current = false;
    }
  };
  return (
    <div className="identity-controls">
      {error && <p role="alert">{error}</p>}
      {receipt ? (
        <>
          <p role="status">
            Invitation created. Delivery is processed separately; confirm the recipient received it before
            relying on email. Share this token privately only with the intended recipient.
          </p>
          <label>
            Invitation token
            <input readOnly value={receipt.token} />
          </label>
          <p>
            The recipient can use the existing Academy “Accept an invitation” option after creating and
            verifying their account.
          </p>
          <button type="button" onClick={onClose}>
            Done
          </button>
        </>
      ) : (
        <form
          onSubmit={e => {
            e.preventDefault();
            void submit();
          }}
        >
          <fieldset disabled={busy}>
            <p>Workspace: {account.session?.workspace.name}</p>
            {!options && (
              <button type="button" onClick={() => setRevision(v => v + 1)}>
                Reload assignment options
              </button>
            )}
            {options && !choices.length && (
              <p role="status">Your current role cannot perform this action in this workspace.</p>
            )}
            <div className="identity-fields">
              <label>
                Recipient {operation === "invitation" ? "email" : "member"}
                {operation === "invitation" ? (
                  <input
                    type="email"
                    required
                    value={recipient}
                    onChange={e => setRecipient(e.target.value)}
                  />
                ) : (
                  <select
                    aria-label="Recipient member"
                    required
                    value={recipient}
                    onChange={e => setRecipient(e.target.value)}
                  >
                    <option value="">Choose verified member</option>
                    {options?.recipients.map(r => (
                      <option key={r.id} value={r.id}>
                        {r.name} · {r.email}
                      </option>
                    ))}
                  </select>
                )}
              </label>
              <label>
                Scope
                <select
                  required
                  aria-label="Scope"
                  value={scopeIndex}
                  onChange={e => {
                    setScopeIndex(e.target.value);
                    setRole("");
                    setExpiry("");
                  }}
                >
                  <option value="">Choose authorized scope</option>
                  {choices.map((c, i) => (
                    <option key={`${c.scopeType}:${c.scopeId}`} value={i}>
                      {c.scopeType === "workspace"
                        ? account.session?.workspace.name
                        : `${c.scopeType} · ${c.scopeId}`}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Role
                <select aria-label="Role" required value={role} onChange={e => setRole(e.target.value)}>
                  <option value="">Choose role</option>
                  {scope?.roles.map(r => (
                    <option key={r.key} value={r.key}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Expiry (your local time)
                <input
                  type="datetime-local"
                  required
                  min={localDate(new Date(Date.now() + 60_000).toISOString())}
                  max={scope?.expiresAt ? localDate(scope.expiresAt) : undefined}
                  value={expiry}
                  onChange={e => setExpiry(e.target.value)}
                />
              </label>
            </div>
            <label>
              Reason
              <textarea
                required
                minLength={5}
                maxLength={2000}
                value={reason}
                onChange={e => setReason(e.target.value)}
              />
            </label>
            {operation === "invitation" && (
              <p>Acceptance and the assigned access are limited by this expiry.</p>
            )}
            {scope?.expiresAt && <p>Authority expires {new Date(scope.expiresAt).toLocaleString()}.</p>}
            {options?.hasMoreRecipients && <p>The first 500 eligible members are shown.</p>}
            <div className="identity-actions">
              <button type="button" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                disabled={!scope || !role || !recipient || reason.trim().length < 5 || !expiry}
              >
                {busy ? "Saving…" : operation === "invitation" ? "Create invitation" : "Assign role"}
              </button>
            </div>
          </fieldset>
        </form>
      )}
      {!receipt && (
        <details>
          <summary>Verify account security for this action</summary>
          <IdentitySecurityControls />
        </details>
      )}
    </div>
  );
}
